import React, { FC } from 'react'
import styled from 'styled-components'
import { Checkbox } from 'ui/checkbox'
import { Input } from 'ui/input'
import { PhoneInput } from 'ui/phone-input'
import { RadioGroup } from 'ui/radio'
import { Select } from 'ui/select'

import { createMediaStyles } from '../../../lib/styled-helpers'
import {
  CheckboxField,
  Field,
  GenericField,
  GroupDeclaration,
  GroupProps,
  HiddenField,
  PhoneField,
  SelectField
} from '../types'

const fieldsAreGeneric = (fields: Field[], groupType: GroupDeclaration['type']): fields is GenericField[] => {
  return groupType === 'text' || groupType === 'radio'
}

const fieldsArePhone = (fields: Field[], groupType: GroupDeclaration['type']): fields is PhoneField[] => {
  return groupType === 'phone'
}

const fieldsAreSelect = (fields: Field[], groupType: GroupDeclaration['type']): fields is SelectField[] => {
  return groupType === 'select'
}

const fieldsAreCheckbox = (fields: Field[], groupType: GroupDeclaration['type']): fields is CheckboxField[] => {
  return groupType === 'checkbox'
}

const fieldsAreHidden = (fields: Field[], groupType: GroupDeclaration['type']): fields is HiddenField[] => {
  return groupType === 'hidden'
}

export const Group: FC<GroupProps & { disabled: boolean }> = ({
  type,
  fields,
  value,
  disabled,
  onFieldChange,
  onNeedFieldValidation,
  errors
}) => {
  const setNewValue = (name: string, value: string | boolean) => {
    return onFieldChange(name, value)
  }

  const handleCheckboxChange = (value: string) => (checked: boolean) => {
    return onFieldChange(value, checked)
  }

  if (fieldsAreHidden(fields, type))
    return (
      <>
        {fields.map((field, index) => (
          <input type={'hidden'} name={field.name} value={field.value} key={index} />
        ))}
      </>
    )

  return (
    <GroupWrapper>
      {type === 'radio' && fieldsAreGeneric(fields, type) && (
        <RadioGroup
          selectedValue={fields && String(value[fields[0].name])}
          fields={fields}
          onChange={(value: string) => setNewValue(fields[0].name, value)}
          disabled={disabled}
        />
      )}

      {type === 'text' &&
        fieldsAreGeneric(fields, type) &&
        fields.map((field, index) => (
          <Input
            {...field}
            type={field.name === 'email' ? 'email' : 'text'}
            inputMode={field.name === 'email' ? 'email' : 'text'}
            block={true}
            key={index}
            value={String(value[field.name])}
            error={errors[field.name]}
            onChange={(value: string) => setNewValue(field.name, value)}
            onBlur={(event) => onNeedFieldValidation && onNeedFieldValidation(event.target.name)}
            disabled={disabled}
          />
        ))}

      {fieldsArePhone(fields, type) &&
        fields.map((field, index) => (
          <PhoneInput
            maskCountryCode={field.code || 'ru'}
            key={index}
            {...field}
            value={String(value[field.name])}
            error={errors[field.name]}
            onChange={(value) => setNewValue(field.name, value)}
            onBlur={(event) => onNeedFieldValidation && onNeedFieldValidation(event.target.name)}
            disabled={disabled}
          />
        ))}

      {fieldsAreCheckbox(fields, type) &&
        fields.map((field, index) => (
          <CheckboxWrapper key={index}>
            <Checkbox
              checked={Boolean(value[field.name])}
              {...field}
              error={errors[field.name]}
              onChange={handleCheckboxChange(field.name)}
              onInput={(event) => {
                onNeedFieldValidation && onNeedFieldValidation((event.target as HTMLInputElement).name)
              }}
              disabled={disabled}
            />
          </CheckboxWrapper>
        ))}

      {fieldsAreSelect(fields, type) &&
        fields.map(
          (field, index) =>
            field.options?.length && (
              <Select
                key={index}
                {...field}
                value={String(value[field.name])}
                onChange={(value) => setNewValue(field.name, value)}
                onInput={(event) => {
                  onNeedFieldValidation && onNeedFieldValidation((event.target as HTMLInputElement).name)
                }}
                options={field.options}
              />
            )
        )}
    </GroupWrapper>
  )
}

const GroupWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing[8]};
    ${createMediaStyles(() => ({
      phone: 'margin-bottom: 12px'
    }))}
  }
  & > * + * {
    margin-top: ${({ theme }) => theme.spacing[8]};
  }
`

const CheckboxWrapper = styled.div`
  & > * {
    width: 100%;
  }
`
